import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import CustomButton from '@/components/inputs/CustomButton'
import { DEFAULT_INTERFACE_LANGUAGE } from '@/constants/interfaceLanguages'
import { USER_ROLES } from '@/constants/userRoles'
import { useAlertContext } from '@/contexts/AlertContext'
import type { Site } from '@/features/site/types/site'
import DeleteUserButton from '@/features/user/components/form/DeleteUserButton'
import FormFieldErrorText from '@/features/user/components/form/FormFieldErrorText'
import ResetPasswordButton from '@/features/user/components/form/ResetPasswordButton'
import SecuritySection from '@/features/user/components/form/SecuritySection'
import UserAccountAccordion from '@/features/user/components/form/UserAccountAccordion'
import UserInterfaceAccordion from '@/features/user/components/form/UserInterfaceAccordion'
import UserRightsAccordion from '@/features/user/components/form/UserRightsAccordion'
import UserSecurityAccordion from '@/features/user/components/form/UserSecurityAccordion'
import { DEFAULT_MFA_SETTINGS } from '@/features/user/constants/mfa'
import { ERROR_NAMES, saveUser as apiSaveUser, updateMfa } from '@/features/user/endpoints/users'
import { useInvalidateUserQuery } from '@/features/user/hooks/useUserQuery'
import { getUserFormSchema } from '@/features/user/schemas'
import type { User, UserForm as UserFormType } from '@/features/user/types/user'
import { UserAuthenticationStatusEnum } from '@/features/user/types/user'
import { getNewPayloadToUpdateMfaSettings } from '@/features/user/utils/mfa'
import type { Partner } from '@/openapi/v1/msa-customer-partner-manager/partner'
import { errorHandler } from '@/utils/errorHandler'

import CountryAccessSection from './form/CountryAccessSection'
import CredentialsSection from './form/CredentialsSection'
import InformationSection from './form/InformationSection'
import InterfaceSection from './form/InterfaceSection'
import PartnerAccessSection from './form/PartnerAccessSection'
import RoleSection from './form/RoleSection'
import SiteAccessSection from './form/SiteAccessSection'

interface UserFormProps {
  sites: Site[]
  partners: Partner[]
  user?: Partial<User>
}

export const UserForm = ({ sites, partners, user }: UserFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pushAlert } = useAlertContext()
  const invalidateUserQuery = useInvalidateUserQuery()

  const editMode = !!user
  const isPasswordResetAllowed = user?.authenticationStatus === UserAuthenticationStatusEnum.FORCECHANGEPASSWORD
  const allowedRoles = Object.values(USER_ROLES).map((role) => role.value)

  const form = useForm<UserFormType>({
    mode: 'onBlur',
    defaultValues: {
      id: user?.id ?? '',
      username: user?.username ?? '',
      givenName: user?.givenName ?? '',
      familyName: user?.familyName ?? '',
      email: user?.email ?? '',
      phone: user?.phone ?? '',
      role: user?.role ?? undefined,
      allowedCountries: user?.allowedCountries ?? [],
      isRestricted: user?.isRestricted ?? false,
      allowedRoIds: user?.allowedRoIds ?? [],
      partnerCode: user?.partnerCode ?? undefined,
      uiLanguage: user?.uiLanguage ?? DEFAULT_INTERFACE_LANGUAGE.value,
      isActive: user?.isActive ?? true,
      mfaSettings: user?.mfaSettings ?? DEFAULT_MFA_SETTINGS,
    },
    resolver: zodResolver(getUserFormSchema(t)),
  })

  useWatch({
    control: form.control,
  })

  function getPartnerCode(siteUuid: string | undefined) {
    return sites.find((site) => site.uuid === siteUuid)?.partner?.partnerCode
  }

  const { mutate: saveUser, isPending } = useMutation({
    mutationFn: async () => {
      const userPayload = form.getValues()

      return await apiSaveUser(userPayload)
    },
    onError: (error: any) => {
      if (error.response.data.name === ERROR_NAMES.EMAIL_EXISTS) {
        form.setError('email', {
          type: 'alreadyExists',
          message: t('user_form.form.email_exists.error').toString(),
        })
      } else if (error.response.data.name === ERROR_NAMES.USERNAME_EXISTS) {
        form.setError('username', {
          type: 'alreadyExists',
          message: t('user_form.form.username_exists.error').toString(),
        })
      } else if (error.response.data.name === ERROR_NAMES.MFA_UPDATE_FAILED) {
        form.setError('mfaSettings', {
          type: 'updateFailed',
          message: t('user_form.form.mfa_update_failed.error').toString(),
        })
      } else if (
        error.response.data.name === ERROR_NAMES.VALIDATION_FAILED &&
        error.response.data.message.includes('phone')
      ) {
        form.setError('phone', {
          type: 'invalid',
          message: t('user_form.form.phone_invalid.error').toString(),
        })
      }
    },
    onSuccess: async (response: any) => {
      const mfaSettingsPayload = form.getValues('mfaSettings') ?? DEFAULT_MFA_SETTINGS

      // In case the user changed its mfaSettings, we need to call the API to update this information.
      if (JSON.stringify(user?.mfaSettings) !== JSON.stringify(mfaSettingsPayload)) {
        // The response.data is the user.id of the created/updated user.
        await updateMfa(response.data, getNewPayloadToUpdateMfaSettings(mfaSettingsPayload))

        await invalidateUserQuery()
      }

      navigate('/users')
    },
  })

  function onSubmit() {
    const role = form.getValues('role') ?? ''

    if (role === USER_ROLES.CUSTOMER_MANAGERS.value && form.getValues('partnerCode') === '') {
      form.setValue('partnerCode', undefined)
    }

    if (role === USER_ROLES.RESOURCE_OWNERS.value && !form.getValues('partnerCode')) {
      const allowedRoIds = form.getValues('allowedRoIds') ?? []
      const lastSelectedCustomerId = allowedRoIds.at(0)

      if (lastSelectedCustomerId) {
        form.setValue('partnerCode', getPartnerCode(lastSelectedCustomerId))
      }
    }

    // FIXME: reconsider what to do with numeralLanguage, for now it is the same as uiLanguage
    form.setValue('numeralLanguage', form.getValues().uiLanguage)

    saveUser()
  }

  function handleResetPasswordError(err: unknown) {
    const error = errorHandler(err, t('user_form.reset_password.error'))

    pushAlert({
      severity: 'error',
      message: error.message,
    })
  }

  return (
    <FormProvider {...form}>
      <Stack
        noValidate
        aria-label={t('user_form.form.title')}
        component="form"
        direction="column"
        gap={3}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Box>
          <UserAccountAccordion user={user}>
            <CredentialsSection authenticationStatus={user?.authenticationStatus} editMode={editMode} />
            <InformationSection />
            {user?.id && editMode && isPasswordResetAllowed && (
              <Box>
                <ResetPasswordButton userId={user.id} onResetPasswordError={handleResetPasswordError} />
              </Box>
            )}
          </UserAccountAccordion>

          <UserRightsAccordion>
            <RoleSection roleOptions={allowedRoles} />
            <CountryAccessSection />
            <PartnerAccessSection partners={partners} />
            <SiteAccessSection partners={partners} sites={sites} />
          </UserRightsAccordion>

          <UserSecurityAccordion preferredOption={user?.mfaSettings?.preferredOption ?? null}>
            <SecuritySection user={user ?? {}} />
            {form.formState.errors.mfaSettings && (
              <FormFieldErrorText message={form.formState.errors.mfaSettings.message} showIcon={true} />
            )}
          </UserSecurityAccordion>

          <UserInterfaceAccordion defaultExpanded={!editMode}>
            <InterfaceSection />
          </UserInterfaceAccordion>
        </Box>

        <Stack direction="row" gap={1} justifyContent="space-between">
          <Stack direction="row" gap={1} justifyContent="flex-start">
            <CustomButton component={Link} disabled={isPending} to="/users" variant="outlined">
              {t('common.button.cancel')}
            </CustomButton>
            <CustomButton disabled={isPending} type="submit" variant="contained">
              {t('common.button.save')}
            </CustomButton>
          </Stack>
          {editMode && <DeleteUserButton disabled={isPending} user={user} />}
        </Stack>
      </Stack>
    </FormProvider>
  )
}
